import React, { createContext, useMemo, ReactElement, useContext, useEffect, ReactNode } from 'react';
import { isSome } from 'fp-ts/lib/Option';
import { Loading, Theme, Result, ResultVariants } from '@dexma/ui-components';
import Branding from 'domain/entity/branding';
import { ReadBranding } from 'use-case/branding/read/readBrandingUseCase';
import { useCase, UseCaseState, UseCaseDispatch, CriteriaCallback } from 'adapter/in/common/hooks/useCase';
import { useProfile } from 'adapter/in/common/context/static/Profile';
import { ReadBrandingCriteriaBuilder } from 'use-case/branding/read/readBrandingCriteria';
import i18n from 'asset/translation';

export type BrandingState = UseCaseState & { branding: Branding };

export const BrandingStateContext = createContext({} as BrandingState);
export const useBranding = (): BrandingState => useContext(BrandingStateContext);

type BrandingDispatch = UseCaseDispatch & { setBranding: (branding: Branding) => void; read: CriteriaCallback };

const BrandingDispatchContext = createContext({} as BrandingDispatch);
export const dispatchBranding = (): BrandingDispatch => useContext(BrandingDispatchContext);

type BrandingProvider = {
    readBranding: ReadBranding;
    children: ReactNode;
};
export const BrandingStaticProvider = (props: BrandingProvider): ReactElement => {
    const { children, readBranding } = props;
    const { profile } = useProfile();
    const { isLoading, data, error, setLoading, setData, setError, execute } = useCase(readBranding, false);
    const valueBrandingDispatch = useMemo(
        () => ({
            setLoading,
            setBranding: setData,
            setError,
            read: execute,
        }),
        [setLoading, setData, setError]
    );

    useEffect(() => {
        execute(ReadBrandingCriteriaBuilder.create().withBrandingId(profile.brandingId).build());
    }, [profile]);
    if (isSome(data)) {
        return (
            <BrandingStateContext.Provider value={{ isLoading, branding: data.value, error }}>
                <BrandingDispatchContext.Provider value={valueBrandingDispatch}>
                    <Theme options={{ primary: data.value.primaryColor }}>{children}</Theme>
                </BrandingDispatchContext.Provider>
            </BrandingStateContext.Provider>
        );
    }
    if (isSome(error)) {
        return (
            <Result
                variant={ResultVariants.ERROR}
                title={i18n.t('common.errorWrong')}
                info={i18n.t('common.errorWrongInfo')}
                icon='bot-dead'
            />
        );
    }
    return <Loading isLoading size={50} className='loading' />;
};
